import { t } from 'i18next';
import { useCallback, useState } from 'react';
import { isEmpty, valuesDifferences } from 'utils/object-helpers';

type Type =
    | 'string'
    | 'number'
    | 'boolean'
    | 'email'
    | 'longitude'
    | 'latitude'
    | 'positive_number'
    | 'zero_or_higer'
    | 'domain'
    | 'discounted_price'
    | 'max_selection';

type ValueType = string | number | boolean;

type DataValidationType = {
    type?: Type;
    required?: boolean;
    maxLength?: number;
    minLength?: number;
    max?: number;
    min?: number;
    regex?: RegExp;
};

export type ValidationSchemaType<T> = {
    [prop in keyof T]?: DataValidationType;
};

export type ValidationErrorType = {
    errorMessage: string;
};

export type ValidationErrorsType<T> = {
    [prop in keyof T]?: ValidationErrorType;
};

export type HandleChangeType<T> = (name: keyof T, value: unknown) => void;

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const LONGITUDE_REGEX = /^[-+]?((0(\.\d{1,7})?|[1-9][0-9]?(\.\d{1,7})?|1[0-7][0-9](\.\d{1,7})?|180(\.0{1,7})?))$/;
const LATITUDE_REGEX = /^[-+]?((0(\.\d{1,6})?|[1-8]?[0-9](\.\d{1,7})?|90(\.0{1,7})?))$/;
const POSITIVE_NUMBER_REGEX = /^[1-9]\d*(\.\d{1,2})?$/;
const ZERO_OR_HIGHER = /^(0|[1-9]\d*)(\.\d{1,2})?$/;
const DOMAIN = /^(?:[a-zA-Z0-9-]{1,63}\.)+[a-zA-Z]{2,}$/;
const DISCOUNTED_PRICE = /^(?:$|^(?!0(\.0{1,2})?$)(?!0\d)(\d{1,9}(\.\d{1,2})?)$)/;
const MAX_SELECTION = /^(|([2-9]|[1-9]\d+))$/;

export const VALIDATORS = {
    type: (value: string, criteria: Type): boolean => {
        switch (criteria) {
            case 'string':
                return typeof value === 'string';
            case 'number':
                return !isNaN(value as unknown as number);
            case 'boolean':
                return typeof value === 'boolean';
            case 'email':
                return EMAIL_REGEX.test(value);
            case 'longitude':
                return LONGITUDE_REGEX.test(value);
            case 'latitude':
                return LATITUDE_REGEX.test(value);
            case 'positive_number':
                return POSITIVE_NUMBER_REGEX.test(value);
            case 'zero_or_higer':
                return ZERO_OR_HIGHER.test(value);
            case 'domain':
                return DOMAIN.test(value);
            case 'discounted_price':
                return DISCOUNTED_PRICE.test(value);
            case 'max_selection':
                return MAX_SELECTION.test(value);
        }
    },
    required: (value: ValueType, criteria: boolean): boolean =>
        !(criteria && (value === '' || value === null || value === undefined)),
    maxLength: (value: string, criteria: number): boolean => value.length <= criteria,
    minLength: (value: string, criteria: number): boolean => value.length >= criteria,
    max: (value: number, criteria: number): boolean => value <= criteria,
    min: (value: number, criteria: number): boolean => value >= criteria,
    regex: (value: string, criteria: RegExp): boolean => criteria.test(value),
};

const validateValue = (value: unknown, schema?: DataValidationType) => {
    if (schema === undefined) return;

    let error: ValidationErrorType | undefined;

    for (const key in schema) {
        const criteria = schema[key];
        const validate = VALIDATORS[key];
        const translation = key === 'type' ? criteria : key;
        if (!validate(value, criteria)) {
            error = { errorMessage: t(`FORM_ERRORS.ERROR_${translation.toUpperCase()}`, { criteria }) };
        }
    }

    return error;
};

const validateValues = (values: object, schema?: DataValidationType) => {
    if (schema === undefined) return;

    const errors = {};
    for (const key in values) {
        const value = values[key];
        const error = validateValue(value, schema[key]);
        if (error) errors[key] = error;
    }
    return errors;
};

type UseFormOptions = {
    trackChangedValues: boolean;
};

type UseFormProps<T> = {
    defaultValues?: T;
    validationSchema?: ValidationSchemaType<T>;
    options?: UseFormOptions;
};

export function useForm<T extends object>(
    props: UseFormProps<T> & { defaultValues: T },
): {
    formData: T;
    setInitialFormData: (initialFormData: T) => void;
    handleSubmit: (callback: () => void) => void;
    handleChange: HandleChangeType<T>;
    errors: ValidationErrorsType<T>;
    isDirty: boolean;
    changedValues: Partial<T>;
};

export function useForm<T extends object>(
    props: UseFormProps<T>,
): {
    formData: T | undefined;
    setInitialFormData: (initialFormData: T) => void;
    handleSubmit: (callback: () => void) => void;
    handleChange: HandleChangeType<T>;
    errors: ValidationErrorsType<T>;
    isDirty: boolean;
    changedValues: Partial<T>;
};

export function useForm<T extends object>({
    defaultValues,
    validationSchema,
    options = {
        trackChangedValues: false,
    },
}: UseFormProps<T>) {
    const [isDirty, setIsDirty] = useState(false);
    const [changedValues, setChangedValues] = useState<Partial<T>>({});
    const [formData, setFormData] = useState(defaultValues);
    const [initialFormData, setInitialFormDataState] = useState(defaultValues);
    const [errors, setErrors] = useState<ValidationErrorsType<T>>({});

    const setInitialFormData = useCallback((initialFormData: T) => {
        setFormData(initialFormData);
        setInitialFormDataState(initialFormData);
    }, []);

    const handleChange = useCallback<HandleChangeType<T>>(
        (name, value) => {
            setFormData((prev) => {
                const formData = { ...prev, [name]: value ?? null } as T;
                if (options.trackChangedValues) {
                    const changedData = valuesDifferences(formData, initialFormData);
                    setChangedValues(changedData);
                    setIsDirty(!isEmpty(changedData));
                }
                return formData;
            });

            if (validationSchema === undefined) return;

            setErrors((prev) => {
                const errors = { ...prev };
                const error = validateValue(value ?? null, validationSchema[name]);
                if (error) errors[name] = error;
                else delete errors[name];
                return errors;
            });
        },
        [validationSchema, options.trackChangedValues, initialFormData],
    );

    const handleSubmit = useCallback(
        (callback: () => void) => {
            if (formData !== undefined) {
                const errors = validateValues(formData, validationSchema) as ValidationErrorsType<T>;
                if (isEmpty(errors)) callback();
                else setErrors(errors);
            }
        },
        [formData, validationSchema],
    );

    return { formData, setInitialFormData, handleSubmit, handleChange, errors, isDirty, changedValues };
}
// export function useForm<T extends object>({
//     defaultValues,
//     validationSchema,
//     options = {
//         trackChangedValues: false,
//     },
// }: UseFormProps<T>) {
//     const [isDirty, setIsDirty] = useState(false);
//     const [changedValues, setChangedValues] = useState<Partial<T>>({});
//     const [formData, setFormData] = useState(defaultValues);
//     const [initialFormData, setInitialFormDataState] = useState(defaultValues);
//     const [errors, setErrors] = useState<ValidationErrorsType<T>>({});
//     const [disableSubmit, setDisableSubmit] = useState(true);

//     const setInitialFormData = useCallback((initialFormData: T) => {
//         setFormData(initialFormData);
//         setInitialFormDataState(initialFormData);
//     }, []);

//     const validationTimeout = useRef<NodeJS.Timeout | null>(null);

//     const handleDisableSubmit = useCallback(
//         (formData: T, name: keyof T, value: unknown) => {
//             if (!validationSchema) return;

//             const errors = validateValues(formData, validationSchema) as ValidationErrorsType<T>;
//             if (isEmpty(errors)) setDisableSubmit(false);
//             else setDisableSubmit(true);

//             setErrors((prev) => {
//                 const newErrors = { ...prev };
//                 const error = validateValue(value ?? null, validationSchema[name]);
//                 if (error) newErrors[name] = error;
//                 else delete newErrors[name];
//                 return newErrors;
//             });
//         },
//         [validationSchema],
//     );

//     const handleChange = useCallback<HandleChangeType<T>>(
//         (name, value) => {
//             if (validationTimeout.current) clearTimeout(validationTimeout.current);
//             validationTimeout.current = setTimeout(() => {
//                 setFormData((prev) => {
//                     const formData = { ...prev, [name]: value ?? null } as T;

//                     if (options.trackChangedValues) {
//                         const changedData = valuesDifferences(formData, initialFormData);
//                         setChangedValues(changedData);
//                         setIsDirty(!isEmpty(changedData));
//                     }
//                     handleDisableSubmit(formData, name, value);

//                     return formData;
//                 });
//             }, 1);
//         },
//         [options.trackChangedValues, initialFormData, handleDisableSubmit],
//     );

//     const handleSubmit = useCallback(
//         (callback: () => void) => {
//             if (formData !== undefined) {
//                 const errors = validateValues(formData, validationSchema) as ValidationErrorsType<T>;
//                 if (isEmpty(errors)) callback();
//                 else setErrors(errors);
//             }
//         },
//         [formData, validationSchema],
//     );

//     return { formData, setInitialFormData, handleSubmit, handleChange, errors, isDirty, changedValues, disableSubmit };
// }
